import Vue from 'vue'
import Base from './base'
import api from '../api/v1'
import { cancelActiveSuggestedCoursesPoll, fetchSuggestedCoursesWithPolling, handleSuggestedCoursesFetchError } from './driversOccupancySuggestedCourses'

const tableName = 'driversOccupancy'
const VIEW_TYPE = tableName

export const DRIVERS_OCCUPANCY_VIEW = {
  LIST: 'list',
  SUGGESTED_LEAVE: 'suggestedLeave',
  SUGGESTED_PICKUP: 'suggestedPickup',
  COURSE_SELECTION: 'courseSelection'
}

const readJSON = source => JSON.parse(JSON.stringify(source))

const moveColumnToStart = (parameters, columnName) => {
  const index = parameters.findIndex(parameter => parameter.name === columnName)
  if (index <= 0) return parameters

  const column = parameters[index]
  return [column, ...parameters.filter((_, columnIndex) => columnIndex !== index)]
}

const moveColumnToEnd = (parameters, columnName) => {
  const index = parameters.findIndex(parameter => parameter.name === columnName)
  if (index === -1 || index === parameters.length - 1) return parameters

  const column = parameters[index]
  return [...parameters.filter((_, columnIndex) => columnIndex !== index), column]
}

// 'expand' i 'actions' muszą zawsze zostać odpowiednio na początku/końcu tabeli, niezależnie od personalizacji użytkownika
const pinFixedTableColumns = parameters => moveColumnToEnd(moveColumnToStart(parameters, 'expand'), 'actions')

const parseTableParameters = (tableParameters, savedTable) => {
  const { hiddenParameters, parameters: savedTableParameters = [] } = savedTable
  const indexMap = new Map()
  savedTableParameters.forEach((element, index) => {
    indexMap.set(element.name, index)
  })
  const orderIndex = name => (indexMap.has(name) ? indexMap.get(name) : Number.MAX_SAFE_INTEGER)
  tableParameters.sort((a, b) => orderIndex(a.name) - orderIndex(b.name))
  return pinFixedTableColumns(
    [...tableParameters]
      .sort((x, y) => {
        const existsX = hiddenParameters.includes(x.name)
        const existsY = hiddenParameters.includes(y.name)
        if (existsX && !existsY) return 1
        if (!existsX && existsY) return -1
        if (existsX && existsY) return hiddenParameters.indexOf(x.name) - hiddenParameters.indexOf(y.name)
        return 0
      })
      .map(config => ({ ...config, show: !hiddenParameters?.includes(config.name) }))
  )
}

const defaultParameters = [
  { name: 'expand', text: '', value: 'expand', show: true, sortable: false, width: 60 },
  { name: 'name', text: 'Kierowca', value: 'name', show: true, sortable: true, width: 166 },
  { name: 'available', text: 'Dostępność', value: 'available', show: true, sortable: true, width: 120 },
  { name: 'direction', text: 'Kierunek', value: 'direction', show: true, sortable: true, width: 100 },
  {
    name: 'timeToDestination',
    text: 'Czas do celu',
    value: 'timeToDestination',
    show: true,
    sortable: true,
    width: 122
  },
  {
    name: 'remainingCoursesCount',
    text: 'Pozostało kursów',
    value: 'remainingCoursesCount',
    show: true,
    sortable: true,
    width: 152
  },
  { name: 'allCoursesCount', text: 'Liczba kursów', value: 'allCoursesCount', show: true, sortable: true, width: 132 },
  { name: 'loopCount', text: 'Pętle', value: 'loopCount', show: true, sortable: true, width: 80 },
  { name: 'distanceCovered', text: 'Kilometry', value: 'distanceCovered', show: true, sortable: true, width: 105 },
  {
    name: 'distanceToDestination',
    text: 'Odległość od celu',
    value: 'distanceToDestination',
    show: true,
    sortable: true,
    width: 155
  },
  {
    name: 'containerTypeVolume',
    text: 'Pojemność kontenera',
    value: 'containerTypeVolume',
    show: true,
    sortable: true,
    width: 173
  },
  { name: 'vehicleType', text: 'Typ pojazdu', value: 'vehicleType', show: true, sortable: true, width: 120 },
  { name: 'trailerId', text: 'Przyczepa', value: 'trailerId', show: true, sortable: true, width: 110 },
  { name: 'courseType', text: 'Typ kursu', value: 'courseType', show: true, sortable: true, width: 140 },
  { name: 'clientName', text: 'Klient', value: 'clientName', show: true, sortable: true, width: 200 },
  {
    name: 'addressDisplayName',
    text: 'Adres',
    value: 'addressDisplayName',
    show: true,
    sortable: true,
    width: 140
  },
  { name: 'actions', text: '', value: 'actions', show: true, sortable: false, width: 80 }
]

const getDriverContext = (rootState, driver) => {
  const departmentId = rootState.core.department?.id
  const driverId = driver?.id

  return { departmentId, driverId }
}

const driverMatchesSearchCriteria = (record, searchQuery) => {
  if (!searchQuery) return true

  const name = `${record.firstName || ''} ${record.lastName || ''}`.trim().toLowerCase()
  return name.includes(searchQuery.toLowerCase())
}

const syncSelectedDriver = (state, record) => {
  if (state.selectedDriver?.id === record.id) {
    state.selectedDriver = {
      ...state.selectedDriver,
      ...record
    }
  }
}

const driversOccupancy = () => {
  const base = new Base(tableName)

  const mutations = {
    SET_OCCUPANCY_VISIBILITY(state, isOpen) {
      state.isOccupancyDialogOpen = isOpen
    },
    SET_QUERY(state, query) {
      state.query = query
    },
    SET_PARAMETERS(state, parameters) {
      state.parameters = pinFixedTableColumns(parameters)
    },
    SET_TABLE_PERSONALIZATION(state, parameters) {
      const newParameters = []
      parameters.forEach(item => {
        const existing = state.parameters.find(p => p.name === item.name)
        if (existing) newParameters.push({ ...existing, show: item.show })
      })
      state.parameters = pinFixedTableColumns(newParameters)
    },
    SET_VIEW_STEP(state, viewStep) {
      state.viewStep = viewStep
    },
    SET_SELECTED_DRIVER(state, driver) {
      state.selectedDriver = driver
    },
    SET_SELECTED_LEAVE_COURSE(state, course) {
      state.selectedLeaveCourse = course
    },
    SET_SELECTED_PICKUP_COURSE(state, course) {
      state.selectedPickupCourse = course
    },
    SET_SENDING_COURSES(state, isSending) {
      state.isSendingCourses = isSending
    },
    SET_SUGGESTED_LEAVE_LOADING(state, isLoading) {
      state.isSuggestedLeaveLoading = isLoading
    },
    SET_SUGGESTED_LEAVE_COURSES(state, courses) {
      state.suggestedLeaveCourses = courses
    },
    SET_SUGGESTED_LEAVE_ERROR(state, error) {
      state.suggestedLeaveError = error
    },
    SET_SUGGESTED_PICKUP_LOADING(state, isLoading) {
      state.isSuggestedPickupLoading = isLoading
    },
    SET_SUGGESTED_PICKUP_COURSES(state, courses) {
      state.suggestedPickupCourses = courses
    },
    SET_SUGGESTED_PICKUP_ERROR(state, error) {
      state.suggestedPickupError = error
    },
    CLEAR_SUGGESTED_PICKUP_STATE(state) {
      state.isSuggestedPickupLoading = false
      state.suggestedPickupCourses = null
      state.suggestedPickupError = null
      state.selectedPickupCourse = null
    },
    CLEAR_SUGGESTED_COURSES_SELECTION(state) {
      state.selectedLeaveCourse = null
      state.selectedPickupCourse = null
      state.isSuggestedLeaveLoading = false
      state.suggestedLeaveCourses = null
      state.suggestedLeaveError = null
      state.isSuggestedPickupLoading = false
      state.suggestedPickupCourses = null
      state.suggestedPickupError = null
    },
    RESET_OCCUPANCY_FLOW(state) {
      state.viewStep = DRIVERS_OCCUPANCY_VIEW.LIST
      state.selectedDriver = null
      state.selectedLeaveCourse = null
      state.selectedPickupCourse = null
      state.isSuggestedLeaveLoading = false
      state.suggestedLeaveCourses = null
      state.suggestedLeaveError = null
      state.isSuggestedPickupLoading = false
      state.suggestedPickupCourses = null
      state.suggestedPickupError = null
      state.isSendingCourses = false
    },
    DELETE_ITEM_WITH_OFFSET(state, { deletedItemId }) {
      const { items, pageOffset } = state
      const filteredItems = [...items].filter(item => deletedItemId !== item.id)
      if (filteredItems.length !== items.length) {
        state.items = filteredItems
        state.totalItemsCount -= 1
        state.pageOffset.excludedOffset = pageOffset.excludedOffset + 1
      }
      if (state.selectedDriver?.id === deletedItemId) {
        state.selectedDriver = null
      }
    },
    UPDATE_ITEMS_WITH_OFFSET(state, records) {
      const updatedItemIds = records.map(record => record.id)
      state.items = state.items.map(item => {
        if (!updatedItemIds.includes(item.id)) return item
        const record = records.find(currentRecord => currentRecord.id === item.id)
        const updatedItem = { ...item, ...record }
        syncSelectedDriver(state, updatedItem)
        return updatedItem
      })
    },
    UPDATE_CERTAIN_ITEMS(state, records) {
      records.forEach(record => {
        const index = state.items.findIndex(item => item.id === record.id)
        if (index === -1) return

        const updatedItem = { ...state.items[index], ...record }
        Vue.set(state.items, index, updatedItem)
        syncSelectedDriver(state, updatedItem)
      })
    }
  }

  const isSwapLeaveCourse = course => course?.swapCourseId != null

  const getters = {
    selectedCourseIds: state => ({
      leaveCourseId: state.selectedLeaveCourse?.courseId ?? null,
      pickupCourseId: state.selectedPickupCourse?.courseId ?? null
    }),
    isSwapLeaveCourse: state => isSwapLeaveCourse(state.selectedLeaveCourse),
    canProceedToCourseSelection: state => state.selectedLeaveCourse?.courseId != null || state.selectedPickupCourse?.courseId != null
  }

  const actions = {
    setOccupancyVisibility({ commit, dispatch }, isShown) {
      if (isShown) {
        dispatch('initTablePersonalization')
        dispatch('getDriversOccupancy')
      } else {
        dispatch('resetOccupancyFlow')
      }
      commit('SET_OCCUPANCY_VISIBILITY', isShown)
    },
    resetOccupancyFlow({ commit }) {
      cancelActiveSuggestedCoursesPoll()
      commit('RESET_OCCUPANCY_FLOW')
    },
    backToDriversList({ dispatch }) {
      dispatch('resetOccupancyFlow')
    },
    backToSuggestedLeave({ commit }) {
      cancelActiveSuggestedCoursesPoll()
      commit('CLEAR_SUGGESTED_PICKUP_STATE')
      commit('SET_VIEW_STEP', DRIVERS_OCCUPANCY_VIEW.SUGGESTED_LEAVE)
    },
    backToSuggestedPickup({ commit }) {
      commit('SET_SELECTED_PICKUP_COURSE', null)
      commit('SET_VIEW_STEP', DRIVERS_OCCUPANCY_VIEW.SUGGESTED_PICKUP)
    },
    backFromCourseSelection({ state, dispatch }) {
      if (isSwapLeaveCourse(state.selectedLeaveCourse)) {
        return dispatch('backToSuggestedLeave')
      }

      return dispatch('backToSuggestedPickup')
    },
    goToCourseSelection({ commit, state, dispatch }) {
      if (!state.selectedLeaveCourse?.courseId && !state.selectedPickupCourse?.courseId) {
        dispatch(
          'snackbar/showSnackbar',
          {
            message: ['Wybierz kurs podstawienia lub zabrania, aby kontynuować'],
            type: 'error'
          },
          { root: true }
        )
        return
      }

      commit('SET_VIEW_STEP', DRIVERS_OCCUPANCY_VIEW.COURSE_SELECTION)
    },
    selectSuggestedPickupCourse({ commit, dispatch }, pickupCourse) {
      commit('SET_SELECTED_PICKUP_COURSE', pickupCourse)
      dispatch('goToCourseSelection')
    },
    skipSuggestedPickup({ commit, dispatch, state }) {
      cancelActiveSuggestedCoursesPoll()
      commit('SET_SUGGESTED_PICKUP_LOADING', false)

      if (state.selectedLeaveCourse?.courseId != null) {
        dispatch('goToCourseSelection')
      }
    },
    async sendSelectedCourses({ state, commit, dispatch }, { dueDate, receiverData }) {
      if (state.isSendingCourses) return

      const driverId = state.selectedDriver?.id
      const isSwap = isSwapLeaveCourse(state.selectedLeaveCourse)
      const coursesToSend = []

      if (state.selectedLeaveCourse?.courseId != null) {
        coursesToSend.push({
          courseId: state.selectedLeaveCourse.courseId,
          label: isSwap ? 'wymiany' : 'podstawienia'
        })
      }

      if (!isSwap && state.selectedPickupCourse?.courseId != null) {
        coursesToSend.push({
          courseId: state.selectedPickupCourse.courseId,
          label: 'zabrania'
        })
      }

      if (!driverId || !coursesToSend.length) {
        dispatch(
          'snackbar/showSnackbar',
          {
            message: ['Brak danych do wysłania kursów'],
            type: 'error'
          },
          { root: true }
        )
        return
      }

      commit('SET_SENDING_COURSES', true)

      const sendParams = {
        driverId,
        dueDate,
        receiverData
      }

      try {
        for (const course of coursesToSend) {
          await api.sendCourse({
            id: course.courseId,
            params: sendParams
          })
        }

        const message = isSwap ? 'Wysłano kurs wymiany' : coursesToSend.length > 1 ? 'Wysłano kursy podstawienia i zabrania' : `Wysłano kurs ${coursesToSend[0].label}`

        dispatch(
          'snackbar/showSnackbar',
          {
            message: [message],
            type: 'success'
          },
          { root: true }
        )

        dispatch('setOccupancyVisibility', false)
        dispatch('getDriversOccupancy', { includeTimes: true })
      } finally {
        commit('SET_SENDING_COURSES', false)
      }
    },
    async loadSuggestedLeaveCourses({ commit, rootState, dispatch }, driver) {
      cancelActiveSuggestedCoursesPoll()
      commit('CLEAR_SUGGESTED_COURSES_SELECTION')
      commit('SET_SELECTED_DRIVER', driver)
      commit('SET_VIEW_STEP', DRIVERS_OCCUPANCY_VIEW.SUGGESTED_LEAVE)
      commit('SET_SUGGESTED_LEAVE_LOADING', true)
      commit('SET_SUGGESTED_LEAVE_ERROR', null)
      commit('SET_SUGGESTED_LEAVE_COURSES', null)

      const { departmentId, driverId } = getDriverContext(rootState, driver)

      if (!departmentId || !driverId) {
        commit('SET_SUGGESTED_LEAVE_ERROR', 'Brak danych kierowcy lub oddziału')
        commit('SET_SUGGESTED_LEAVE_LOADING', false)
        return
      }

      try {
        const courses = await fetchSuggestedCoursesWithPolling(() => api.suggestLeave({ driverId, departmentId }))
        commit('SET_SUGGESTED_LEAVE_COURSES', courses)
      } catch (error) {
        const { cancelled, message } = handleSuggestedCoursesFetchError({
          error,
          dispatch,
          fallbackMessage: 'Nie udało się pobrać sugerowanych kursów podstawienia'
        })

        if (!cancelled) {
          commit('SET_SUGGESTED_LEAVE_ERROR', message)
        }
      } finally {
        commit('SET_SUGGESTED_LEAVE_LOADING', false)
      }
    },
    skipToSuggestedPickup({ commit, dispatch }) {
      cancelActiveSuggestedCoursesPoll()
      commit('SET_SELECTED_LEAVE_COURSE', null)
      commit('SET_SUGGESTED_LEAVE_LOADING', false)
      return dispatch('fetchSuggestedPickupCourses')
    },
    selectSuggestedLeaveCourse({ commit, dispatch }, leaveCourse) {
      commit('SET_SELECTED_LEAVE_COURSE', leaveCourse)

      if (isSwapLeaveCourse(leaveCourse)) {
        return dispatch('goToCourseSelection')
      }

      return dispatch('fetchSuggestedPickupCourses')
    },
    async fetchSuggestedPickupCourses({ state, commit, rootState, dispatch }) {
      cancelActiveSuggestedCoursesPoll()

      const driver = state.selectedDriver
      const { departmentId, driverId } = getDriverContext(rootState, driver)

      if (!departmentId || !driverId) {
        commit('SET_SUGGESTED_PICKUP_ERROR', 'Brak danych kierowcy lub oddziału')
        commit('SET_SUGGESTED_PICKUP_LOADING', false)
        return
      }

      commit('SET_VIEW_STEP', DRIVERS_OCCUPANCY_VIEW.SUGGESTED_PICKUP)
      commit('SET_SUGGESTED_PICKUP_LOADING', true)
      commit('SET_SUGGESTED_PICKUP_ERROR', null)
      commit('SET_SUGGESTED_PICKUP_COURSES', null)

      const requestParams = { driverId, departmentId }
      if (state.selectedLeaveCourse?.courseId != null) {
        requestParams.leaveCourseId = state.selectedLeaveCourse.courseId
      }

      try {
        const courses = await fetchSuggestedCoursesWithPolling(() => api.suggestPickup(requestParams))
        commit('SET_SUGGESTED_PICKUP_COURSES', courses)
      } catch (error) {
        const { cancelled, message } = handleSuggestedCoursesFetchError({
          error,
          dispatch,
          fallbackMessage: 'Nie udało się pobrać sugerowanych kursów zabrania'
        })

        if (!cancelled) {
          commit('SET_SUGGESTED_PICKUP_ERROR', message)
        }
      } finally {
        commit('SET_SUGGESTED_PICKUP_LOADING', false)
      }
    },
    initTablePersonalization({ rootState, commit }) {
      const savedTable = rootState.tables?.savedTableSettings?.[VIEW_TYPE]
      const parameters = savedTable?.hiddenParameters ? parseTableParameters(readJSON(defaultParameters), savedTable) : readJSON(defaultParameters)
      commit('SET_PARAMETERS', parameters)
    },
    setTablePersonalization({ state, commit, rootState }, { parameters }) {
      commit('SET_TABLE_PERSONALIZATION', parameters)
      const payload = {
        ...rootState.tables?.savedTableSettings?.[VIEW_TYPE],
        hiddenParameters: state.parameters.filter(p => !p.show).map(p => p.name),
        parameters: state.parameters.map(p => ({ ...p, show: !p.show }))
      }
      // Keep tables.savedTableSettings in sync — initTablePersonalization reads from it on reopen
      commit('tables/SET_TABLE_SETTINGS', { viewType: VIEW_TYPE, settings: payload }, { root: true })
      return api.updateUserSettings({ viewType: VIEW_TYPE, value: JSON.stringify(payload) })
    },
    setDriversQuery({ commit, dispatch }, query) {
      commit('SET_QUERY', query)
      commit('tables/SET_TABLE_SEARCH', { tableName, search: query }, { root: true })
      dispatch('getDriversOccupancy')
    },
    getItems({ dispatch }) {
      return dispatch('getDriversOccupancy')
    },
    getDriversOccupancy({ state, rootState, commit }, { includeTimes, driverId } = { includeTimes: false, driverId: undefined }) {
      commit('SET_PROCESSING', true)
      const departmentId = rootState.core.department.id
      const query = state.query || undefined
      return api
        .getDriversOccupancy({ departmentId, includeTimes, driverId, query })
        .then(resp => {
          if (driverId) {
            commit('UPDATE_CERTAIN_ITEMS', [resp.data[0]])
          } else {
            commit('SET_ITEMS', { items: resp.data })
            commit('SET_ITEMS_COUNT', resp.data.length)
          }
        })
        .finally(() => {
          commit('SET_PROCESSING', false)
        })
    },
    updateItemsWS({ state, getters, commit }, { actionType, records, deletedItemId, updateCertainItems }) {
      if (actionType === 'delete') {
        commit('DELETE_ITEM_WITH_OFFSET', { deletedItemId })
        return
      }
      console.log('updateItemsWS', actionType, records, deletedItemId, updateCertainItems)

      const searchQuery = state.query
      const normalizedRecords = (records || []).map(record => record.data || record)
      const filteredRecords = normalizedRecords.filter(record => driverMatchesSearchCriteria(record, searchQuery))

      if (actionType === 'create') {
        commit('CREATE_ITEMS_WITH_OFFSET', filteredRecords)
        return
      }

      if (actionType === 'update') {
        const newRecords = []
        const updatedRecords = []

        normalizedRecords.forEach(record => {
          if (getters.currentListIds.includes(record.id)) {
            updatedRecords.push(record)
          } else if (driverMatchesSearchCriteria(record, searchQuery)) {
            newRecords.push(record)
          }
        })

        normalizedRecords
          .filter(record => !driverMatchesSearchCriteria(record, searchQuery) && getters.currentListIds.includes(record.id))
          .forEach(record => {
            commit('DELETE_ITEM_WITH_OFFSET', { deletedItemId: record.id })
          })

        if (newRecords.length) commit('CREATE_ITEMS_WITH_OFFSET', newRecords)
        if (updatedRecords.length) {
          if (updateCertainItems) {
            commit('UPDATE_CERTAIN_ITEMS', updatedRecords)
          } else {
            commit('UPDATE_ITEMS_WITH_OFFSET', updatedRecords)
          }
        }
      }
    }
  }

  base.state = {
    ...base.state,
    isOccupancyDialogOpen: false,
    query: '',
    parameters: readJSON(defaultParameters),
    viewStep: DRIVERS_OCCUPANCY_VIEW.LIST,
    selectedDriver: null,
    selectedLeaveCourse: null,
    isSuggestedLeaveLoading: false,
    suggestedLeaveCourses: null,
    suggestedLeaveError: null,
    isSuggestedPickupLoading: false,
    suggestedPickupCourses: null,
    suggestedPickupError: null,
    selectedPickupCourse: null,
    isSendingCourses: false
  }
  base.mutations = {
    ...base.mutations,
    ...mutations
  }
  base.actions = {
    ...base.actions,
    ...actions
  }
  base.getters = {
    ...base.getters,
    ...getters
  }

  return base
}

export default driversOccupancy
